/**
 * God of Creation - Web Audio API Sound Effects System
 * Toss WebView compatible (no HTMLAudioElement)
 */

export type SfxName =
  | 'ui-tap'
  | 'slot-place'
  | 'combine-start'
  | 'combine-success'
  | 'first-discovery'
  | 'milestone'
  | 'popup-close';

const SFX_PATHS: Record<SfxName, string> = {
  'ui-tap': '/sfx/ui-tap.mp3',
  'slot-place': '/sfx/slot-place.mp3',
  'combine-start': '/sfx/combine-start.mp3',
  'combine-success': '/sfx/combine-success.mp3',
  'first-discovery': '/sfx/first-discovery.mp3',
  'milestone': '/sfx/milestone.mp3',
  'popup-close': '/sfx/popup-close.mp3',
};

let ctx: AudioContext | null = null;
let gainNode: GainNode | null = null;
const bufferCache = new Map<string, AudioBuffer>();
let muted = false;

function getContext(): AudioContext {
  if (!ctx) {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    ctx = new AudioCtx();
    gainNode = ctx.createGain();
    gainNode.gain.value = 0.6;
    gainNode.connect(ctx.destination);
  }
  return ctx;
}

async function loadBuffer(path: string): Promise<AudioBuffer | null> {
  if (bufferCache.has(path)) return bufferCache.get(path)!;
  try {
    const response = await fetch(path);
    const arrayBuffer = await response.arrayBuffer();
    const audioCtx = getContext();
    const buffer = await audioCtx.decodeAudioData(arrayBuffer);
    bufferCache.set(path, buffer);
    return buffer;
  } catch {
    return null;
  }
}

export function preloadSfx(): void {
  for (const path of Object.values(SFX_PATHS)) {
    loadBuffer(path);
  }
}

export async function playSfx(name: SfxName): Promise<void> {
  if (muted) return;
  const audioCtx = getContext();
  if (audioCtx.state === 'suspended') {
    await audioCtx.resume();
  }
  const path = SFX_PATHS[name];
  const buffer = await loadBuffer(path);
  if (!buffer || !gainNode) return;

  const source = audioCtx.createBufferSource();
  source.buffer = buffer;
  source.connect(gainNode);
  source.start(0);
}

export function setSfxMuted(value: boolean): void {
  muted = value;
}

export function isSfxMuted(): boolean {
  return muted;
}

export function suspendSfx(): void {
  if (ctx && ctx.state === 'running') {
    ctx.suspend();
  }
}

export function resumeSfx(): void {
  if (ctx && ctx.state === 'suspended') {
    ctx.resume();
  }
}
